<template lang="pug">
    .ma-sm-2
        v-container
            v-form
                v-row(align="center")
                    v-col(cols='6')
                        v-text-field(label='Department' type='text' v-model="searchObj.department_name")
                    v-col(cols='6')
                        v-text-field(label='Employee' type='text' v-model="searchObj.user_email_or_full_name")
                v-row(align="center")
                    v-col(cols='12')
                        .text-right
                            v-btn.mr-4(
                                @click='resetSearch()'
                            )
                                v-icon mdi-refresh
                                | Reset
                            v-btn.mr-4(
                                color='info'
                                @click='searchContacts()'
                                :disabled="disableSearchButton"
                            )
                                v-icon mdi-magnify
                                | Search
        
        .text-center(v-if="isLoading")
            v-progress-circular(
                :size='100'
                :width='10'
                color='var(--volie-blue)'
                indeterminate
                style="margin-top: 100px;"
            )


        v-alert(
            outlined
            type='info'
            dense
            border='left'
            style="margin-top: 16px;"
            v-else-if="departments.length === 0"
        )
            | Unable to find any contacts. Please adjust your search criteria.

        v-card.mx-auto.mt-md-4.mb-md-4(
            v-else-if="departments && departments.length > 0"
            v-for='department in departments'
            :key='department.department_id'
            outlined
        )

            v-toolbar.volie-v-toolbar-color(dark dense)
                v-icon(style="margin-right: 5px;") mdi-office-building
                v-toolbar-title Department: {{ department.department_name }}

            div(v-if="department.contacts && department.contacts.length > 0")
                v-simple-table
                    template(v-slot:default)
                        thead
                            tr
                                th.table-cell-padding
                                    .text-subtitle-1.font-weight-medium Contacts
                                    .text-caption
                                        | Successful call transfers today:
                                        strong {{ (department.daily_completed_call_transfers || 0) | number }}
                        tbody
                            tr(v-for='contact in department.contacts' :key='contact.contact_id')
                                td.table-cell-padding
                                    .text-capitalize.text-overline.text--secondary.text--darken-3 {{ contact.friendly_contact_method }}:
                                    | {{ contact.friendly_value }}
                                    template(v-if="contact.friendly_extension_main_line")
                                        |  · Main line {{ contact.friendly_extension_main_line }}

                                    div(v-if="contact.notes && contact.notes.length > 0")
                                        .text-capitalize.caption.text--secondary.text--darken-3 Notes:
                                        .communication-screen-contact-notes-margin(v-html="contact.notes")
                                    
                                    .text-center(v-if="canTransferContact(contact) && validCall && showTransferMenu")
                                        template(v-if="allowedExternalBlindTransfersEnabled")
                                            v-btn.ma-2.mt-4(outlined x-small color="primary" light @click="startContactTransfer('department', 'blind', department.department_id, contact);")
                                                v-icon mdi-eye-off
                                                | Blind Transfer
                                        template(v-if="allowedExternalWarmTransfersEnabled")
                                            v-btn.ma-2.mt-4(outlined x-small color="error" light @click="startContactTransfer('department', 'warm', department.department_id, contact);")
                                                v-icon mdi-fire
                                                | Warm Transfer

            v-divider.divider-spacing
            .text-subtitle-2.employee-subtitle.text-center
                | Employees

            div(v-if="department.employees && department.employees.length > 0")
                div(v-for='(employee, idx) in department.employees' :key="employee.employee_id + '-' + idx")
                    v-card.employee-card(
                        outlined
                    )
                        v-simple-table
                            template(v-slot:default)
                                thead
                                    tr
                                        th.table-cell-padding
                                            .text-subtitle-2 {{ employee.full_name }}
                                            .caption
                                                | Job Title: 
                                                strong(v-if="employee.job_title_id > 0") {{ employee.job_title_name }}
                                                template(v-else) N/a
                                            .caption
                                                | Successful call transfers today: 
                                                strong {{ (employee.daily_completed_call_transfers || 0) | number }}
                                tbody
                                    tr(v-for='contact in employee.contacts' :key='contact.contact_id')
                                        td.table-cell-padding
                                            .text-capitalize.text-overline.text--secondary.text--darken-3 {{ contact.friendly_contact_method }}: 
                                            | {{ contact.friendly_value }}
                                            template(v-if="contact.friendly_extension_main_line")
                                                |  · Main line {{ contact.friendly_extension_main_line }}
                                            div(v-if="contact.notes && contact.notes.length > 0")
                                                .text-capitalize.caption.text--secondary.text--darken-3 Notes:
                                                .communication-screen-contact-notes-margin(v-html="contact.notes")

                                            .text-center(v-if="canTransferContact(contact) && validCall")
                                                v-btn.ma-2.mt-4(outlined x-small color="primary" light @click="startContactTransfer('employee', 'blind', employee.employee_id, contact);")
                                                    v-icon mdi-eye-off
                                                    | Blind Transfer
                                                v-btn.ma-2.mt-4(outlined x-small color="error" light @click="startContactTransfer('employee', 'warm', employee.employee_id, contact);")
                                                    v-icon mdi-fire
                                                    | Warm Transfer
                    template(v-if="idx < department.employees.length - 1")
                        v-divider.divider-spacing
                ShowMoreRowNew(
                    v-if="department.department_name === 'Not Assigned'"
                    :handle-show-more="handleShowMore"
                    :loading="showMoreLoading"
                    :show-more="showMore"
                )

            v-simple-table(v-else)
                template(v-slot:default)
                    tbody
                        tr
                            td.text-center
                                .caption
                                    | No employees setup for this department

</template>

<script lang="ts">
    import Vue from 'vue';
    import VolieState from '@/store';

    // Data
    import { GetCommunicationCenterOrganizationContacts, GetEmployees } from "@/data";
    import { CheckFeatureFlag } from '@/data/feature_flag_check';

    // Components
    import ShowMoreRowNew from '@/components/shared/show_more_row_new.vue';

    // Constants
    import { FeatureFlags } from '@/constants/feature_flags';

    // Helpers
    import { isShowMore } from '@/helpers/show_more';

    export default Vue.extend({
        data() {
            return {
                departments: [],
                isLoading: false,
                limit: 50,
                offset: 0,
                showMoreLoading: false,
                notAssignedDepartment: {
                    department_name: 'Not Assigned',
                    employees: [],
                },
                searchObj: {
                    department_name: '',
                    user_email_or_full_name: '',
                },
                showMore: true,
                sipOutboundTransferEnabled: false,
            }
        },
        methods: {
            canTransferContact(contact) {
                if (!contact?.contact_method) {
                    return false;
                }
                if ([1, 2, 3, 7, 8].indexOf(contact.contact_method) >= 0) {
                    return true;
                }
                // Extension transfers need a main line and the same SIP outbound flag as the dialog
                return contact.contact_method === 5
                    && !!contact.extension_main_line
                    && this.sipOutboundTransferEnabled;
            },
            async checkSipOutboundTransferFlag() {
                this.sipOutboundTransferEnabled = !!(await CheckFeatureFlag(FeatureFlags.SipOutboundTransfer).catch(() => false));
            },
            getCommunicationCenterOrganizationContacts() {

                // Verify
                if (!this.organizationId) {
                    return;
                }

                const options = {
                    organization_id: this.organizationId,
                    department_name: this.searchObj.department_name.trim() || '',
                    user_email_or_full_name: this.searchObj.user_email_or_full_name.trim() || '',
                }

                if (this.searchObj.department_name.trim() !== '') {
                    this.notAssignedDepartment.employees = [];
                    this.departments = this.departments.filter(
                        d => d.department_name !== 'Not Assigned'
                    );
                }

                this.isLoading = true;

                GetCommunicationCenterOrganizationContacts(options).then(async (response) => {
                    if (response?.Data?.departments?.length > 0) {
                        this.departments = response.Data.departments;

                        // check if the not assigned department was received from backend
                        // if not received and not filtering by department name, request not assigned employees separately
                        const notAssignedDepartmentIndex = this.departments.findIndex(department => department.department_name === 'Not Assigned');
                        if (this.searchObj.department_name.trim() === '') {
                            if (notAssignedDepartmentIndex === -1) {
                                this.notAssignedDepartment.employees = await this.getEmployeesNotAssignedToDepartment();
                            }
                        }

                    } else {
                        this.departments = [];
                    }
                    
                }).finally(() => {
                    this.isLoading = false;
                });
            },
            async getEmployeesNotAssignedToDepartment() {
                if (this.searchObj.department_name.trim() !== '') {
                    return [];
                }

                const options = {
                    organization_id: this.organizationId,
                    department_name: 'Not Assigned',
                    exclude_click_to_call_employees: true,
                    include_contacts: true,
                    limit: this.limit || 50,
                    offset: this.offset || 0,
                }

                if (this.notAssignedDepartment?.employees?.length > 0) {
                    this.showMoreLoading = true;
                }

                GetEmployees(options).then((response) => {
                    if (response?.Data?.employees?.length > 0) {
                        if (this.notAssignedDepartment.employees?.length > 0) {
                            this.notAssignedDepartment.employees = [
                                ...this.notAssignedDepartment.employees,
                                ...response.Data.employees
                            ];
                        } else {
                            this.notAssignedDepartment.employees = response.Data.employees;
                            this.departments.push(this.notAssignedDepartment);
                        }

                    }

                    this.offset += this.limit;
                    this.showMore = isShowMore(response?.Data?.employees?.length, this.limit);

                }).finally(() => {
                    this.showMoreLoading = false;
                });
            },
            handleShowMore() {
                this.getEmployeesNotAssignedToDepartment();
            },
            startContactTransfer(destination, type, destinationId, contact) {
                if (contact?.contact_method === 5) {
                    this.openTransferDialog(destination, 'external', type, destinationId, contact.extension_main_line, contact.value);
                    return;
                }
                this.openTransferDialog(destination, 'external', type, destinationId, contact.value);
            },
            resetSearch() {
                this.searchObj = {
                    department_name: '',
                    user_email_or_full_name: '',
                };
                this.offset = 0;
                this.showMore = true;
                this.searchContacts();
            },
            searchContacts() {
                this.getCommunicationCenterOrganizationContacts();
            },
        },
        computed: {
            allowedExternalBlindTransfersEnabled() {
                return this.currentUser?.account?.enabled_transfers?.includes("external_blind_transfer");
            },
            allowedExternalWarmTransfersEnabled() {
                return this.currentUser?.account?.enabled_transfers?.includes("external_warm_transfer");
            },
            currentUser() {
                return VolieState.currentUser();
            },
            disableSearchButton() {
                return !(this.searchObj?.department_name?.length > 0 || this.searchObj?.user_email_or_full_name?.length > 0);
            },
            showTransferMenu() {
                return this.allowedExternalBlindTransfersEnabled || this.allowedExternalWarmTransfersEnabled;
            },
            validCall() {
                return this.callId > 0 && this.onCall === true;
            },
        },
        components: {
            ShowMoreRowNew,
        },
        props: {
            callId: Number,
            onCall: Boolean,
            openTransferDialog: Function,
            organizationId: Number,
        },
        created() {
            this.checkSipOutboundTransferFlag();
            this.resetSearch();
        },
    });
</script>

<style lang="scss" scoped>
    #app {
        .table-cell-padding {
            padding-top: 10px;
            padding-bottom: 10px;
        }

        .employee-subtitle {
            margin-bottom: 20px;
        }

        .employee-card {
            margin: 10px 10px 10px 10px;
        }

        .communication-screen-contact-notes-margin {
            margin-top: 5px;
            margin-left: 20px;
            margin-right: 20px;
        }
    }
</style>
